// src/request/client-identity.ts
var SAFE = /^[A-Za-z0-9._-]+$/;
function formatClientIdentity({ app, version, sha }) {
  const name = app?.trim();
  if (!name || !SAFE.test(name)) return void 0;
  if (typeof version !== "number" || !Number.isInteger(version) || version < 1) return void 0;
  const build = sha?.trim() || "dev";
  if (!SAFE.test(build)) return void 0;
  return `${name};v=${version};sha=${build}`;
}

// src/request/usecase.ts
import {
  MOVE_TYPE_USECASES,
  UseCaseKey,
  usecaseKeyForMoveType
} from "@hopdrive/sdk-moves";
var CANONICAL_PLANNER_USECASE_KEYS = Object.freeze(Object.values(UseCaseKey));
var PLANNER_USECASE_TABLE = MOVE_TYPE_USECASES;
function usecaseKeyForPlannerChoice(type, direction) {
  return usecaseKeyForMoveType(type, direction);
}

// src/request/build-request.ts
var MoveRequestPlanError = class extends Error {
  constructor(message) {
    super(message);
    this.name = "MoveRequestPlanError";
  }
};
var TYPES = [...new Set(PLANNER_USECASE_TABLE.map((row) => row.type))];
var TWO_LEG_TYPES = /* @__PURE__ */ new Set(["round-trip", "concierge-loaner"]);
var CONCIERGE_TYPES = /* @__PURE__ */ new Set(["concierge", "concierge-loaner"]);
function text(value) {
  if (typeof value !== "string") return void 0;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : void 0;
}
function positiveInt(value) {
  return typeof value === "number" && Number.isInteger(value) && value > 0 ? value : void 0;
}
function finite(value) {
  return typeof value === "number" && Number.isFinite(value) ? value : void 0;
}
function instant(value) {
  const raw = text(value);
  if (!raw) return void 0;
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? void 0 : raw;
}
function vehicle(value) {
  if (!value) return void 0;
  const mapped = {};
  const vin = text(value.vin);
  if (vin) mapped.vin = vin;
  const make = text(value.make);
  if (make) mapped.make = make;
  const model = text(value.model);
  if (model) mapped.model = model;
  const year = text(value.year);
  if (year) mapped.year = year;
  const color = text(value.color);
  if (color) mapped.color = color;
  const stock = text(value.stock);
  if (stock) mapped.stock = stock;
  const odometer = finite(value.odometer);
  if (odometer !== void 0) mapped.odometer = odometer;
  if (typeof value.manual === "boolean") mapped.manual = value.manual;
  const grossWeightLbs = finite(value.grossWeightLbs);
  if (grossWeightLbs !== void 0) mapped.grossWeightLbs = grossWeightLbs;
  return Object.keys(mapped).length > 0 ? mapped : void 0;
}
function record(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return void 0;
  const entries = Object.entries(value);
  return entries.length > 0 ? value : void 0;
}
function consumerFirst(value) {
  if (value === "loaner") return "loaner";
  if (value === "consumer" || value === "customer") return "customer";
  return void 0;
}
function buildVehicleMoveRequest(state) {
  const customerId = positiveInt(state.rooftopId);
  if (!customerId) throw new MoveRequestPlanError("Choose a customer rooftop before submitting.");
  const requestType = TYPES.find((candidate) => candidate === state.type);
  if (!requestType) throw new MoveRequestPlanError("Choose a move type before submitting.");
  const laneId = positiveInt(state.mainLaneId);
  if (!laneId) throw new MoveRequestPlanError("The pickup and delivery lane could not be resolved.");
  const readyBy = instant(state.readyBy);
  if (!readyBy) throw new MoveRequestPlanError("Choose a valid date and time before submitting.");
  const direction = state.consumerAction === "pickup" || state.consumerAction === "return" ? state.consumerAction : void 0;
  const usecaseKey = usecaseKeyForPlannerChoice(requestType, direction);
  if (!usecaseKey) {
    throw new MoveRequestPlanError("Choose whether this is a pickup or a return before submitting.");
  }
  const request = { customerId, usecaseKey, requestType, laneId, readyBy };
  const productKey = text(state.productKey);
  if (productKey) request.productKey = productKey;
  if (CONCIERGE_TYPES.has(requestType)) {
    request.direction = direction;
    const name = text(state.consumerName);
    if (name) request.consumerName = name;
    const phone = text(state.consumerPhone);
    if (phone) request.consumerPhone = phone;
  }
  if (requestType === "concierge-loaner") {
    const first = consumerFirst(state.consumerFirst);
    if (!first) {
      throw new MoveRequestPlanError("Choose which vehicle moves first (the loaner order) before submitting.");
    }
    request.consumerFirst = first;
  }
  const inverseLaneId = positiveInt(state.inverseLaneId);
  if (TWO_LEG_TYPES.has(requestType)) {
    if (!inverseLaneId) {
      throw new MoveRequestPlanError("The second leg\u2019s lane could not be resolved.");
    }
    request.secondLeg = buildSecondLeg(state, inverseLaneId, readyBy);
  } else if (inverseLaneId) {
    request.inverseLaneId = inverseLaneId;
  }
  const vehicle1 = vehicle(state.vehicle1);
  if (vehicle1) request.vehicle1 = vehicle1;
  const referenceNum = text(state.referenceNum1);
  if (referenceNum) request.referenceNum = referenceNum;
  const dealerContact = text(state.contact1);
  if (dealerContact) request.dealerContact = dealerContact;
  const moveDetails = text(state.notes1);
  if (moveDetails) request.moveDetails = moveDetails;
  const payerId = positiveInt(state.payerId);
  if (payerId) request.payerId = payerId;
  const workflowsetId = positiveInt(state.workflowsetId);
  if (workflowsetId) request.workflowsetId = workflowsetId;
  const pickupWorkflowData = record(state.pickupWorkflowData1);
  if (pickupWorkflowData) request.pickupWorkflowData = pickupWorkflowData;
  const deliveryWorkflowData = record(state.deliveryWorkflowData1);
  if (deliveryWorkflowData) request.deliveryWorkflowData = deliveryWorkflowData;
  request.scheduled = state.isScheduleTimeline === true;
  const notifyEmails = (state.emails ?? []).map(text).filter((email) => !!email);
  if (notifyEmails.length > 0) request.notifyEmails = notifyEmails;
  if (Number.isInteger(state.driverRowNumber) && state.driverRowNumber !== null) {
    request.driverRowNumber = state.driverRowNumber;
  }
  const workflowResponses = record(state.workflowResponses);
  if (workflowResponses) request.workflowResponses = workflowResponses;
  const contacts = contactsFrom(state.contactInfo);
  if (contacts) request.contacts = contacts;
  const clientRequestId = text(state.clientRequestId);
  if (clientRequestId) request.clientRequestId = clientRequestId;
  const clientIdentity = formatClientIdentity({ app: state.clientApp, version: state.clientVersion, sha: state.buildSha });
  if (clientIdentity) request.clientIdentity = clientIdentity;
  return request;
}
function contactEnd(info, end) {
  const contact = {};
  const name = text(info[`${end}Name`]);
  const mobileNumber = text(info[`${end}Mobile`]) ?? text(info[`${end}Phone`]);
  const landlineNumber = text(info[`${end}Landline`]);
  const email = text(info[`${end}Email`]);
  const notes = text(info[`${end}Notes`]);
  if (!name && !mobileNumber && !landlineNumber && !email) return void 0;
  if (name) contact.name = name;
  if (mobileNumber) contact.mobileNumber = mobileNumber;
  if (landlineNumber) contact.landlineNumber = landlineNumber;
  if (email) contact.email = email;
  if (notes) contact.notes = notes;
  return contact;
}
function contactsFrom(info) {
  if (!info) return void 0;
  const pickup = contactEnd(info, "pickup");
  const delivery = contactEnd(info, "delivery");
  if (!pickup && !delivery) return void 0;
  return { ...pickup ? { pickup } : {}, ...delivery ? { delivery } : {} };
}
function buildSecondLeg(state, laneId, firstLegReadyBy) {
  const leg = { laneId };
  const readyBy = instant(state.secondLegReadyBy);
  if (readyBy) leg.readyBy = readyBy;
  else if (state.secondLegReadyBy != null) {
    throw new MoveRequestPlanError("The second leg\u2019s date and time is not valid.");
  } else leg.readyBy = firstLegReadyBy;
  const vehicle2 = vehicle(state.vehicle2);
  if (vehicle2) leg.vehicle = vehicle2;
  const referenceNum = text(state.referenceNum2);
  if (referenceNum) leg.referenceNum = referenceNum;
  const dealerContact = text(state.contact2);
  if (dealerContact) leg.dealerContact = dealerContact;
  const moveDetails = text(state.notes2);
  if (moveDetails) leg.moveDetails = moveDetails;
  const workflowsetId = positiveInt(state.secondLegWorkflowsetId);
  if (workflowsetId) leg.workflowsetId = workflowsetId;
  const pickupWorkflowData = record(state.pickupWorkflowData2);
  if (pickupWorkflowData) leg.pickupWorkflowData = pickupWorkflowData;
  const deliveryWorkflowData = record(state.deliveryWorkflowData2);
  if (deliveryWorkflowData) leg.deliveryWorkflowData = deliveryWorkflowData;
  return leg;
}

// src/request/types.ts
var RIDE_ROLE = "ride";

// src/request/read-result.ts
var MoveResultError = class extends Error {
  constructor(message) {
    super(message);
    this.name = "MoveResultError";
  }
};
function readVehicleMoveResult(result) {
  const primary = result?.move;
  const primaryMoveId = usableId(primary);
  if (!primaryMoveId) {
    throw new MoveResultError("The move request returned no move. Please check the dashboard before retrying.");
  }
  const peers = Array.isArray(result?.peerMoves) ? result.peerMoves : [];
  const commercialPeers = peers.filter((move) => move?.role !== RIDE_ROLE);
  return {
    scenario: result.scenario,
    primaryMoveId,
    moveIds: [primaryMoveId, ...ids(commercialPeers)],
    rideMoveIds: ids(peers.filter((move) => move?.role === RIDE_ROLE)),
    allMoveIds: [primaryMoveId, ...ids(peers)],
    replayed: result.replayed === true,
    contactsSaved: typeof result.contactsSaved === "boolean" ? result.contactsSaved : null
  };
}
function usableId(move) {
  const id = move?.id;
  return typeof id === "number" && Number.isInteger(id) && id > 0 ? id : void 0;
}
function ids(moves) {
  return moves.map(usableId).filter((id) => id !== void 0);
}

// src/request/errors.ts
var GENERIC_MOVE_REQUEST_MESSAGE = "Failed to plan moves! Please refresh and try again. If the problem persists, please contact us.";
var MESSAGES = {
  unauthenticated: "Your session has expired. Please sign in again and resubmit.",
  forbidden: "You do not have permission to create moves for this rooftop.",
  forbidden_payer: "You cannot bill this move to the selected payer. Choose a different payer.",
  payer_required: "This rooftop requires a third-party payer. Choose one before submitting.",
  product_not_owned: "No active contract covers this move type for this rooftop. Contact us to add it before submitting.",
  product_ambiguous: "More than one active contract covers this move type, so we cannot tell which to bill. Please contact us.",
  product_unavailable: "This move type is currently unavailable for this rooftop. Please contact us if you expected it to be active.",
  invalid_workflowset: "The selected workflow is unavailable for this rooftop. Choose another and resubmit.",
  invalid_lane: "One of the selected locations does not belong to this rooftop. Check the lane and resubmit.",
  invalid_input: "Some of the details entered are not valid. Check the form and resubmit.",
  internal_error: "The move could not be saved. Please try again in a moment.",
  // Upper case because the SDK's location gate owns this code, not the Action,
  // which rethrows it untouched. A full-service move needs one end inside a
  // live region, so a refusal means both ends are outside.
  OUTSIDE_REGION: "Neither the pickup nor the delivery address is in a region HopDrive serves yet. Choose a location inside our service area, or contact us about this one.",
  // Upper case for the same reason: the SDK refuses a Valet Lite move whose
  // two ends are both outside the rooftop's service radius.
  OUTSIDE_SERVICE_RADIUS: "Both the pickup and delivery addresses are outside your rooftop's service radius. Choose a location within your service area, or contact us to change the radius.",
  // Upper case for the same reason: the SDK refuses a declared product that
  // does not sell the requested move type, and the Action rethrows it.
  PRODUCT_USECASE_INCOMPATIBLE: "The selected product does not offer this move type. Choose a different product or move type and resubmit.",
  // Upper case for the same reason: the SDK refuses a lane it cannot time
  // (#3447). Submit waits for both lanes to be ready, so these mean the route
  // changed state between the button enabling and the request landing.
  LANE_NOT_READY: "The route between these addresses is still being calculated. Wait a moment and resubmit.",
  LANE_ENRICHMENT_FAILED: "We could not calculate a route between these addresses. Check both addresses, or contact us.",
  // Upper case because the server's build floor owns it, not the Action: the
  // request may be fine, the bundle sending it is too old. Where the app has
  // an updater, its dialog replaces this message (see plannerSubmit.ts).
  CLIENT_UPDATE_REQUIRED: "This page is out of date. Save or discard your changes, then it will reload."
};
var CLIENT_UPDATE_REQUIRED = "CLIENT_UPDATE_REQUIRED";
function vehicleMoveErrorCode(error) {
  const errors = error?.graphQLErrors;
  if (!Array.isArray(errors)) return void 0;
  for (const candidate of errors) {
    const code = graphQLErrorCode(candidate);
    if (code) return code;
  }
  return void 0;
}
function graphQLErrorCode(error) {
  const extensions = error?.extensions;
  const direct = extensions?.code;
  if (typeof direct === "string" && direct.length > 0) return direct;
  const nested = extensions?.internal?.error?.code;
  if (typeof nested === "string" && nested.length > 0) return nested;
  return void 0;
}
function vehicleMoveErrorMessage(error) {
  const code = vehicleMoveErrorCode(error);
  if (code && code in MESSAGES) return MESSAGES[code];
  return GENERIC_MOVE_REQUEST_MESSAGE;
}
function isClientUpdateRequiredError(error) {
  return vehicleMoveErrorCode(error) === CLIENT_UPDATE_REQUIRED;
}
function isRetryableMoveRequestError(error) {
  const code = vehicleMoveErrorCode(error);
  return code === void 0 || code === "internal_error";
}

// src/request/intended-product.ts
import { ProductSelectionOutcome, selectOwnedProduct } from "@hopdrive/sdk-moves";
async function resolveIntendedProduct(input) {
  const usecaseKey = usecaseKeyForPlannerChoice(input.type, input.direction);
  if (!usecaseKey) return { outcome: "undecided", productKey: null, candidates: [] };
  const selection = await selectOwnedProduct({
    usecaseKey,
    ownedProductKeys: input.ownedProductKeys,
    canCreateNew: input.canCreateNew
    // No tie-break, matching the Action. Guessing between two Valet tiers
    // would quote a rate the move may not bill.
  });
  switch (selection.outcome) {
    case ProductSelectionOutcome.SELECTED:
      return { outcome: "selected", productKey: selection.product_key, candidates: selection.candidates };
    case ProductSelectionOutcome.NONE_OWNED:
      return { outcome: "none-owned", productKey: null, candidates: [] };
    case ProductSelectionOutcome.AMBIGUOUS:
      return { outcome: "ambiguous", productKey: null, candidates: selection.candidates };
    case ProductSelectionOutcome.UNAVAILABLE:
      return { outcome: "unavailable", productKey: selection.product_key, candidates: selection.candidates };
  }
}

// src/request/move-types.ts
var MOVE_PLANNER_TYPES = Object.freeze([
  {
    value: "concierge",
    label: "Valet",
    description: `Bring in your customer's vehicle for service or return your customer's vehicle to their home (includes SMS notifications).`,
    color: "secondary"
  },
  {
    value: "concierge-loaner",
    label: "Valet + Loaner",
    description: `Perform the same valet service while also assigning a loaner vehicle to your customer (includes SMS notifications).`,
    color: "secondary"
  },
  {
    value: "one-way",
    label: "One-Way",
    description: `Simply move one of your vehicles from point A to point B.`,
    color: "primary"
  },
  {
    value: "round-trip",
    label: "Round-Trip",
    description: `Swap two of your vehicles between point A and point B.`,
    color: "primary"
  }
]);
function resolveMovePlannerTypes(overrides) {
  if (!overrides) return MOVE_PLANNER_TYPES;
  return MOVE_PLANNER_TYPES.map((option) => {
    const override = overrides[option.value];
    if (!override) return option;
    return {
      ...option,
      label: override.label || option.label,
      description: override.description || option.description
    };
  });
}

export {
  formatClientIdentity,
  CANONICAL_PLANNER_USECASE_KEYS,
  PLANNER_USECASE_TABLE,
  usecaseKeyForPlannerChoice,
  MoveRequestPlanError,
  buildVehicleMoveRequest,
  RIDE_ROLE,
  MoveResultError,
  readVehicleMoveResult,
  GENERIC_MOVE_REQUEST_MESSAGE,
  CLIENT_UPDATE_REQUIRED,
  vehicleMoveErrorCode,
  graphQLErrorCode,
  vehicleMoveErrorMessage,
  isClientUpdateRequiredError,
  isRetryableMoveRequestError,
  resolveIntendedProduct,
  MOVE_PLANNER_TYPES,
  resolveMovePlannerTypes
};
//# sourceMappingURL=chunk-WEMXH2UM.mjs.map